import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { logError } from '@zg-rentals/log-error';

import ManagePaymentMethodsPage from './component';
import paths from '@renter-hub/pathsManifest';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import {
  deletePaymentMethod,
  getRachelDashboard,
  requestInitiatePayment,
  requestMoveInCostsPayment,
  requestUpdateModeLinkToken,
} from '@renter-hub/actions/requests';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import TransactionConstants from '@common/constants/TransactionConstants';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_DELETED_SUCCESS,
  PAYMENT_METHOD_INSUFFICIENT_FUNDS,
  PAYMENT_METHOD_UNPROCESSABLE,
  PAYMENT_SCHEDULED_SUCCESS,
  PAYMENT_TIMEOUT,
  PAYMENT_UPDATED_SUCCESS,
  PLAID_CREATE_PAYMENT_METHOD_ERROR,
} from '@renter-hub/constants/notifications';
import { ErrorInterface } from '@common/types';
import HTTP_STATUS from '@common/constants/HttpConstants';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import { RequestMoveInCostsPaymentInterface } from '@renter-hub/models/RequestsTypes';
import { REQUEST_TIMEOUT } from '../../../utils/customErrorTypes';
import { trackValidationError } from '@common/actions/TrackingActions';

const mapStateToProps = (state) => ({
  addressInfo: state.payments.renter.dashboard.rentTerms.addressInfo,
  paymentMethods: state.payments.renter.dashboard.paymentMethods,
  paymentSubscriptionDetails: state.payments.renter.dashboard.paymentDetails.paymentSubscriptionDetails,
  moveInCostsDetails: state.payments.renter.dashboard.moveInCostsDetails,
  nextPayable: state.payments.renter.dashboard.nextPayable,
  nextScheduleMonth: state.payments.renter.dashboard.nextScheduleMonth,
  paymentMethodTypes: state.payments.renter.dashboard.paymentMethodTypes,
  transactions: state.payments.renter.dashboard.paymentHistory.transactions,
  currentScheduleMonth: state.payments.renter.dashboard.currentScheduleMonth,
  config: state.payments.renter.config,
});

const mapDispatchToProps = (dispatch, { baseUrl, location, brand, propertyId }) => ({
  onCancel: () => {
    // redirect to either the previous page, or the dashboard (default)
    const fromWhere = location?.state?.fromWhere;
    const toUrl = fromWhere === paths.paymentSettingsPage ? baseUrl + paths.paymentSettingsPage : baseUrl;

    return dispatch(push(toUrl));
  },
  onAddNewPaymentClick: () => {
    return dispatch(push(baseUrl + paths.editPaymentInfoPage));
  },
  onDeletePaymentMethodClick: async (paymentMethodIdToDelete, paymentMethods) => {
    try {
      const deletePaymentMethodResponse = await dispatch(deletePaymentMethod(paymentMethodIdToDelete));
      await checkRequestActionSuccess(deletePaymentMethodResponse);
      await dispatch(getRachelDashboard({ propertyId }));
      await dispatch(push(baseUrl));
      const paymentMethodInfo = paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodIdToDelete);
      await dispatch(
        showPageNotification(PAYMENT_METHOD_DELETED_SUCCESS, { paymentMethodDeleted: paymentMethodInfo?.displayName }),
      );
    } catch (error) {
      logError({ error });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  getPlaidUpdateModeLinkToken: (externalId) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(requestUpdateModeLinkToken(externalId)))
      .catch((err) => {
        logError({ error: err });
        return dispatch(showPageNotification(DEFAULT_ERROR, { brand }));
      }),
  onPaymentMethodCreationCompletion: async ({ errorMessage, paymentMethod }) => {
    try {
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      await dispatch(push(baseUrl));
      // Show the payment updated success banner
      await dispatch(showPageNotification(PAYMENT_UPDATED_SUCCESS));

      return { error: false };
    } catch (err) {
      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;
      const error = err as ErrorInterface;
      const message = err.message ?? '';

      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 422 status, show payment method unprocessable banner
        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          notificationType = PAYMENT_METHOD_UNPROCESSABLE;
        }
      }

      logError({ error: err });
      if (message) {
        await dispatch(showModalNotification(PLAID_CREATE_PAYMENT_METHOD_ERROR, { message }));
      } else {
        await dispatch(showModalNotification(notificationType));
      }
      return { error: true };
    }
  },
  onSubmitDeletePaymentMethod: async ({
    moveInCostsPaymentMethodId,
    monthlyRentPaymentMethodId,
    paymentMethodIdToDelete,
    paymentSubscriptionDetails,
    moveInCostsDetails,
    nextPayable,
    nextScheduleMonth,
    transactions,
    currentScheduleMonth,
  }) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      const currentTransaction = transactions.find(
        (transaction) => currentScheduleMonth.scheduleMonth === transaction.scheduleMonth,
      );

      const isTransactionPaid = currentTransaction?.renterStatus === TransactionConstants.STATUS.COMPLETE;
      if (paymentSubscriptionDetails?.paymentMethodId) {
        const createPaymentPayload = {
          scheduleMonth: isTransactionPaid ? nextScheduleMonth.scheduleMonth : nextPayable.scheduleMonth,
          paymentDate: paymentSubscriptionDetails.nextScheduledDate,
          encodedPaymentMethodId: monthlyRentPaymentMethodId,
          recurring: paymentSubscriptionDetails.recurring,
        };

        const createPaymentResponse = await dispatch(requestInitiatePayment(createPaymentPayload));
        await checkRequestActionSuccess(createPaymentResponse);
      }

      if (moveInCostsDetails?.paymentMethodId) {
        const createMoveInCostsPayload: RequestMoveInCostsPaymentInterface = {
          paymentDate: moveInCostsDetails.sendDate,
          groupKey: moveInCostsDetails.groupKey,
          encodedPaymentMethodId: moveInCostsPaymentMethodId,
        };
        const createMoveInCostsPaymentResponse = await dispatch(requestMoveInCostsPayment(createMoveInCostsPayload));
        await checkRequestActionSuccess(createMoveInCostsPaymentResponse);
      }

      const deletePaymentMethodResponse = await dispatch(deletePaymentMethod(paymentMethodIdToDelete));
      await checkRequestActionSuccess(deletePaymentMethodResponse);

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate to the dashboard
      await dispatch(push(baseUrl));

      // Show the rent payment success banner
      await dispatch(showPageNotification(PAYMENT_SCHEDULED_SUCCESS));

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;

      if (error.action && error.action.payload) {
        const { status, response } = error.action.payload;
        const { messageCode } = response || {};

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 408 status, show timeout message and redirect to dashboard
        if (status === HTTP_STATUS.SC_REQUEST_TIMEOUT) {
          logError({ error: err, errorType: REQUEST_TIMEOUT, context: { status, messageCode } });

          await dispatch(push(baseUrl));
          notificationType = PAYMENT_TIMEOUT;
        }

        // If 422, show insufficient funds if message code is insufficient_funds
        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY && messageCode === ResponseMessageCode.INSUFFICIENT_FUNDS) {
          notificationType = PAYMENT_METHOD_INSUFFICIENT_FUNDS;
        }
      }

      logError({ error: err, context: { notificationType } });
      dispatch(showModalNotification(notificationType));
      return { error: true };
    }
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    onSubmitDeletePaymentMethod: ({
      moveInCostsPaymentMethodId,
      monthlyRentPaymentMethodId,
      paymentMethodIdToDelete,
    }) =>
      dispatchProps.onSubmitDeletePaymentMethod({
        moveInCostsPaymentMethodId,
        monthlyRentPaymentMethodId,
        paymentMethodIdToDelete,
        paymentSubscriptionDetails: stateProps.paymentSubscriptionDetails,
        nextPayable: stateProps.nextPayable,
        moveInCostsDetails: stateProps.moveInCostsDetails,
        nextScheduleMonth: stateProps.nextScheduleMonth,
        transactions: stateProps.transactions,
        currentScheduleMonth: stateProps.currentScheduleMonth,
      }),
    onDeletePaymentMethodClick: (paymentMethodIdToDelete) =>
      dispatchProps.onDeletePaymentMethodClick(paymentMethodIdToDelete, stateProps.paymentMethods),
  };
};
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ManagePaymentMethodsPage);
